import clsx from 'clsx'
import React from 'react'
import isNumber from 'lodash/isNumber'
import Dialog from '@mui/material/Dialog'
import { makeStyles } from '@mui/styles'
import DialogTitle from '@mui/material/DialogTitle'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Draggable from 'react-draggable'
import Paper from '@mui/material/Paper'
import PropTypes from 'prop-types'
import DragIcon from '@assets/images/icons/drag.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import CloseSvg from '@assets/images/icons/v2/close.svg'

import { Title, SubTitle } from '@/modules/base/components/texts'

const useStyles = makeStyles((props) => ({
  dragHandle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: 10,
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing',
    },
  },
  paper: {
    margin: 0,
    borderRadius: 16,
    padding: ({ paddingtop = 20 }) => `${paddingtop}px 0`,
    minWidth: ({ width = 600, maxWidth = 'auto' }) => width && maxWidth ? `${width}px` : null,
    maxWidth: ({ width = 600, maxWidth = 'auto' }) => width && maxWidth ? `${width}px` : null,
  },
  title: {
    display: 'flex',
    flexDirection: 'column',
    padding: ({ modalspace = 32 }) => `${modalspace}px ${modalspace}px 0 ${modalspace}px`,
  },
  subtitle: {
    padding: ({ modalspace = 32 }) => `0 ${modalspace}px ${modalspace / 2}px ${modalspace}px`,
  },
  content: ({ title, modalspace = 32, firstchildpaddingtop = 0, firstchildpaddingX = 0 }) => ({
    padding: title ? `0 ${modalspace}px` : `${modalspace}px ${modalspace}px 0 ${modalspace}px`,
    overflowX: 'hidden',
    '&:first-child': {
      paddingTop: isNumber(firstchildpaddingtop) ? `${firstchildpaddingtop}px` : 20,
      paddingLeft: isNumber(firstchildpaddingX) && `${firstchildpaddingX}px`,
      paddingRight: isNumber(firstchildpaddingX) && `${firstchildpaddingX}px`,
    },
  }),
  actions: {
    padding: ({ modalspace = 32 }) => `30px ${modalspace}px ${modalspace}px`,
  },
  closeButton: {
    border: 0,
    backgroundColor: 'transparent',
    padding: 10,
    margin: '10px 20px',
    outline: 'none',
    position: 'absolute',
    right: 0,
  },
  ...props.classes,
}))

const PaperComponent = (props) => {
  return (
    <Draggable
      handle="#drag-handle"
      cancel={'[class*="MuiDialogContent-root"]'}
      bounds={'parent'}
    >
      <Paper {...props} />
    </Draggable>
  )
}

const ModalBig = (props) => {
  const {
    title,
    subtitle,
    actions,
    actionClass,
    titleClass,
    dialogContentClass,
    children,
    disableEscapeKeyDown,
    disableBackdropClick,
    onClose,
    draggable,
    showCloseIcon = true,
    className,
    classNameRoot,
    classNameContainer,
    maxWidth: _maxWidth, // unused; prevents react error message for passing prop to Dialog
    dataQsId,
    ...otherProps
  } = props

  const iconSize = '10px'
  const classes = useStyles(props)
  const titleEl = title
    ? <DialogTitle classes={{ root: clsx(classes.title, titleClass) }} disableTypography><Title>{title}</Title></DialogTitle>
    : null
  const actionEl = actions && actions.length
    ? <DialogActions classes={{ root: clsx(classes.actions, actionClass) }}>{actions}</DialogActions>
    : null

  const handleClose = (event, reason) => {
    if (disableBackdropClick && reason === 'backdropClick') {
      return false
    }

    if (disableEscapeKeyDown && reason === 'escapeKeyDown') {
      return false
    }

    if (typeof onClose === 'function') {
      onClose()
    }
  }
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.stopPropagation()
    }
  }

  return (
    <Dialog
      onClose={handleClose}
      {...otherProps}
      PaperComponent={PaperComponent}
      aria-labelledby="drag-handle"
      classes={{
        paper: clsx(classes.paper, className),
        root: classNameRoot,
        container: classNameContainer,
      }}
      data-qs-id={dataQsId}>
      {draggable && <div className={classes.dragHandle} id='drag-handle'><SVGIcon svg={DragIcon} width={'20px'} height={'20px'} /></div>}
      {titleEl}
      {showCloseIcon && (
        <button onClick={onClose} className={classes.closeButton}>
          <SVGIcon svg={CloseSvg} height={iconSize} width={iconSize} customClass={classes.icon} />
        </button>
      )}
      {subtitle && <div className={classes.subtitle}>
        <SubTitle lineHeight={props.lineHeight}>{subtitle}</SubTitle>

      </div>}
      <DialogContent classes={{ root: clsx(classes.content, dialogContentClass && dialogContentClass) }} onKeyPress={handleKeyDown}>
        {children}
      </DialogContent>
      {actionEl}
    </Dialog>
  )
}

ModalBig.propTypes = {
  open: PropTypes.bool,
  className: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  actionClass: PropTypes.string,
  titleClass: PropTypes.string,
  dialogContentClass: PropTypes.string,
  actions: PropTypes.array,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  modalspace: PropTypes.number,
  width: PropTypes.number,
  paddingtop: PropTypes.number,
  firstchildpaddingtop: PropTypes.number,
  false: PropTypes.bool,
  firstchildpaddingX: PropTypes.number,
  maxWidth: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  disableEscapeKeyDown: PropTypes.bool,
  disableBackdropClick: PropTypes.bool,
  onClose: PropTypes.func,
  draggable: PropTypes.bool,
  showCloseIcon: PropTypes.bool,
  classNameRoot: PropTypes.string,
  classNameContainer: PropTypes.string,
  dataQsId: PropTypes.string.isRequired,
  fullScreen: PropTypes.bool,
  style: PropTypes.object,
}

export default ModalBig
